/**
 * Phase 106 (Plan 106-12, D-09, FLEET-CONSOLE-08) — the superadmin grant
 * panel: list / grant / revoke the fleet console's own capability. Rendered
 * in its own section on `FleetConsolePage.tsx`, below the instance table.
 *
 * P-42: the grant control resolves a person to a numeric id through the
 * EXISTING user-list surface (`useAdminUsers`, the same hook the admin user
 * pages already use) — never a free email input. The backend's own contract
 * is id-only for the exact same reason (a typo in an email field could hand
 * fleet control to the wrong person).
 *
 * P-44: revoking the CURRENT user requires an explicit confirmation naming
 * the consequence before the mutation fires — self-revoke ends the actor's
 * own access on their very next request, because `requireSuperadmin`
 * re-reads the capability fresh from the database every time (there is no
 * session or token to invalidate).
 */
import { useState } from 'react';
import { useAuth } from '../../services/authContext';
import { useAdminUsers } from '../../hooks/useAdmin';
import {
  useSuperadmins,
  useGrantSuperadmin,
  useRevokeSuperadmin,
} from '../../hooks/useFleetInstances';
import { ApiClientError } from '../../services/api/client';
import { Button } from '../common/Button';
import { Spinner } from '../common/Spinner';

function displayName(person: { firstName: string | null; lastName: string | null; email: string }): string {
  const name = [person.lastName, person.firstName].filter(Boolean).join(' ');
  return name || person.email;
}

export function SuperadminGrantPanel() {
  const { user: currentUser } = useAuth();
  const { data: holders, isLoading: holdersLoading } = useSuperadmins();
  const { data: allUsers } = useAdminUsers();
  const grantMutation = useGrantSuperadmin();
  const revokeMutation = useRevokeSuperadmin();

  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [grantError, setGrantError] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [confirmingSelfRevoke, setConfirmingSelfRevoke] = useState<number | null>(null);

  const holderIds = new Set((holders ?? []).map((h) => h.id));
  const candidateUsers = (allUsers ?? []).filter(
    (u) => u.deletedAt === null && !holderIds.has(u.id)
  );

  async function handleGrant() {
    const userId = Number(selectedUserId);
    if (!Number.isInteger(userId) || userId <= 0) return;
    setGrantError(null);
    try {
      await grantMutation.mutateAsync(userId);
      setSelectedUserId('');
    } catch (err) {
      setGrantError(err instanceof Error ? err.message : 'Failed to grant the capability.');
    }
  }

  async function performRevoke(userId: number) {
    setRevokeError(null);
    try {
      await revokeMutation.mutateAsync(userId);
      setConfirmingSelfRevoke(null);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setRevokeError(err.message);
      } else {
        setRevokeError(err instanceof Error ? err.message : 'Failed to revoke the capability.');
      }
    }
  }

  function handleRevokeClick(userId: number) {
    if (currentUser && userId === currentUser.id) {
      setConfirmingSelfRevoke(userId);
      return;
    }
    void performRevoke(userId);
  }

  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold text-gray-900">Superadmins</h2>
      <p className="text-sm text-gray-600 max-w-3xl mt-1">
        The superadmin capability is deliberately NOT a role — it cannot be granted from the
        normal admin user surfaces, and it is checked on this page and nowhere else. Any current
        holder can grant it to further holders, so treat this list as the highest-value target on
        this console.
      </p>

      {holdersLoading ? (
        <div className="mt-3">
          <Spinner size="sm" />
        </div>
      ) : (
        <table aria-label="Superadmins" className="mt-3 min-w-full divide-y divide-gray-200 border rounded">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Holder</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Email</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Roles</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(holders ?? []).map((holder) => {
              const isSelf = currentUser?.id === holder.id;
              return (
                <tr key={holder.id}>
                  <td className="px-3 py-2 text-sm">
                    {displayName(holder)}
                    {isSelf && <span className="ml-1 text-xs text-gray-500">(you)</span>}
                  </td>
                  <td className="px-3 py-2 text-sm">{holder.email}</td>
                  <td className="px-3 py-2 text-sm text-gray-500">{holder.roles.join(', ') || '—'}</td>
                  <td className="px-3 py-2 text-sm">
                    {confirmingSelfRevoke === holder.id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-amber-700">
                          This ends YOUR OWN access to this console on your next request.
                        </span>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setConfirmingSelfRevoke(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => void performRevoke(holder.id)}
                          isLoading={revokeMutation.isPending}
                        >
                          Yes, revoke my access
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleRevokeClick(holder.id)}
                        disabled={revokeMutation.isPending}
                      >
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {revokeError && (
        <p className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" role="alert">
          {revokeError}
        </p>
      )}

      <div className="mt-4 flex items-end gap-2">
        <div>
          <label htmlFor="fleet-grant-user-select" className="block text-xs font-medium text-gray-700 mb-1">
            Grant the capability to
          </label>
          <select
            id="fleet-grant-user-select"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm min-w-[16rem]"
          >
            <option value="">Select an existing user&hellip;</option>
            {candidateUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {displayName(u)} ({u.email})
              </option>
            ))}
          </select>
        </div>
        <Button
          onClick={() => void handleGrant()}
          disabled={selectedUserId === '' || grantMutation.isPending}
          isLoading={grantMutation.isPending}
        >
          Grant
        </Button>
      </div>

      {grantError && (
        <p className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" role="alert">
          {grantError}
        </p>
      )}
    </section>
  );
}
